import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ShoppingCart, Plus, Search, Trash2, Send, Package, 
  Check, MessageCircle, Camera, Upload, X, Edit, RefreshCw,
  ChevronDown, ChevronUp, FileText, Phone, AlertTriangle,
  CheckCircle, XCircle, ArrowRight, Info
} from 'lucide-react';
import TenantMobileLayout from './TenantMobileLayout';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Formattazione valuta italiana
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '€0,00';
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
  const headers = { 'Authorization': `Bearer ${token}` };
  let tenantDb = localStorage.getItem('tenant_db');
  if (!tenantDb) {
    try {
      const tenantList = JSON.parse(localStorage.getItem('tenant_list') || '[]');
      if (tenantList.length > 0 && tenantList[0].database_name) {
        tenantDb = tenantList[0].database_name;
      }
    } catch (e) {}
  }
  if (!tenantDb) {
    try {
      const tenantUser = JSON.parse(localStorage.getItem('tenant_user') || '{}');
      if (tenantUser.tenants && tenantUser.tenants[0]) {
        tenantDb = tenantUser.tenants[0].database_name;
      }
    } catch (e) {}
  }
  if (tenantDb) {
    headers['X-Tenant-DB'] = tenantDb.replace(/^tenant_/, '');
  }
  return headers;
};

const TenantMobileOrdini = ({ onBack, setCurrentPage }) => {
  const [activeTab, setActiveTab] = useState('lista'); // lista, ordini, ddt
  const [loading, setLoading] = useState(false);
  
  // Lista della spesa
  const [listaSpesa, setListaSpesa] = useState([]);
  const [searchProdotto, setSearchProdotto] = useState('');
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  
  // Ordini fornitore
  const [ordiniFornitore, setOrdiniFornitore] = useState([]);
  
  // DDT in attesa
  const [ddtAttesa, setDdtAttesa] = useState([]);
  
  // Modals
  const [showQuantitaModal, setShowQuantitaModal] = useState(false);
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [quantita, setQuantita] = useState('');
  const [unitaMisura, setUnitaMisura] = useState('kg');
  
  // Import
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showImportTestoModal, setShowImportTestoModal] = useState(false);
  const [testoImport, setTestoImport] = useState('');
  const [loadingImport, setLoadingImport] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showImportResults, setShowImportResults] = useState(false);

  // Caricamento bolla con ordine
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [showBollaModal, setShowBollaModal] = useState(false);
  const [analyzingBolla, setAnalyzingBolla] = useState(false);
  const [bollaAnalisi, setBollaAnalisi] = useState(null);
  const [confrontoOrdine, setConfrontoOrdine] = useState(null);

  // Carica DDT senza ordine (Nuova Bolla)
  const [showCaricaDDTModal, setShowCaricaDDTModal] = useState(false);
  const [caricaDDTAnalisi, setCaricaDDTAnalisi] = useState(null);
  const [analyzingCaricaDDT, setAnalyzingCaricaDDT] = useState(false);

  // Carica dati
  useEffect(() => {
    loadListaSpesa();
    loadOrdiniFornitore();
    loadDDTAttesa();
  }, []);

  const loadListaSpesa = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ordini/lista-spesa`, { headers: getAuthHeaders() });
      setListaSpesa(res.data.items || []);
    } catch (error) {
      console.error('Errore caricamento lista spesa:', error);
    }
  };

  const loadOrdiniFornitore = async () => {
    try {
      // Carica TUTTI gli ordini (non solo da_inviare)
      const res = await axios.get(`${API}/gestione/ordini-fornitore`, { headers: getAuthHeaders() });
      setOrdiniFornitore(res.data.ordini || []);
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
    }
  };

  const loadDDTAttesa = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ddt?solo_attesa=true`, { headers: getAuthHeaders() });
      const inAttesa = (res.data.ddt || []).filter(d => d.status === 'in_attesa');
      setDdtAttesa(inAttesa);
    } catch (error) {
      console.error('Errore caricamento DDT:', error);
    }
  };

  // Debounce per ricerca prodotti
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchProdotto.length >= 2) {
        searchProdotti();
      } else {
        setSuggerimenti([]);
        setShowSuggerimenti(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchProdotto]);

  const searchProdotti = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ordini/suggerisci-prodotto?q=${encodeURIComponent(searchProdotto)}`, {
        headers: getAuthHeaders()
      });
      setSuggerimenti(res.data.suggerimenti || []);
      setShowSuggerimenti(true);
    } catch (error) {
      console.error('Errore ricerca prodotti:', error);
    }
  };

  const handleSelectProdotto = (prodotto, fornitore) => {
    setSelectedProdotto({ prodotto, fornitore });
    setShowSuggerimenti(false);
    setSearchProdotto('');
    setQuantita('');
    setUnitaMisura(fornitore.unita_misura || 'kg');
    setShowQuantitaModal(true);
  };

  const aggiungiAllaLista = async () => {
    if (!selectedProdotto || !quantita || parseFloat(quantita) <= 0) {
      toast.error('Inserisci una quantità valida');
      return;
    }

    const { prodotto, fornitore } = selectedProdotto;
    
    try {
      await axios.post(`${API}/gestione/ordini/lista-spesa`, {
        nome: prodotto.nome,
        quantita: parseFloat(quantita),
        unita_misura: unitaMisura,
        fornitore_suggerito: fornitore.nome,
        prezzo_stimato: fornitore.ultimo_prezzo
      }, { headers: getAuthHeaders() });
      
      await loadListaSpesa();
      setQuantita('');
      setSelectedProdotto(null);
      setShowQuantitaModal(false);
      toast.success(`${prodotto.nome} aggiunto`);
    } catch (error) {
      toast.error('Errore durante l\'aggiunta');
    }
  };

  const rimuoviDallaLista = async (id) => {
    try {
      await axios.delete(`${API}/gestione/ordini/lista-spesa/${id}`, { headers: getAuthHeaders() });
      await loadListaSpesa();
      toast.success('Rimosso');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const generaOrdiniPerFornitore = async () => {
    if (listaSpesa.length === 0) {
      toast.error('Lista vuota');
      return;
    }

    setLoading(true);
    try {
      // Auto-assegna fornitori prima
      await axios.post(`${API}/gestione/ordini/lista-spesa/auto-assegna-fornitori`, {}, { headers: getAuthHeaders() });
      
      // Crea ordine
      const ordineRes = await axios.post(`${API}/gestione/ordini`, {
        nome: `Ordine ${new Date().toLocaleDateString('it-IT')}`,
        prodotti: listaSpesa.map(item => ({
          nome: item.nome,
          quantita: item.quantita,
          unita_misura: item.unita_misura,
          fornitore_id: item.fornitore_id,
          fornitore_nome: item.fornitore_nome,
          prezzo_stimato: item.prezzo_stimato
        }))
      }, { headers: getAuthHeaders() });

      // Genera ordini per fornitore
      await axios.post(`${API}/gestione/ordini/${ordineRes.data.ordine.id}/genera-per-fornitore`, {}, { headers: getAuthHeaders() });

      // Svuota lista
      await axios.delete(`${API}/gestione/ordini/lista-spesa`, { headers: getAuthHeaders() });

      toast.success('Ordini generati!');
      setListaSpesa([]);
      setActiveTab('ordini');
      loadOrdiniFornitore();
      loadDDTAttesa();
    } catch (error) {
      toast.error('Errore generazione ordini');
    } finally {
      setLoading(false);
    }
  };

  const inviaWhatsApp = async (ordine) => {
    if (!ordine.whatsapp_link) {
      toast.error('WhatsApp non configurato');
      return;
    }
    window.open(ordine.whatsapp_link, '_blank');
    try {
      await axios.put(`${API}/gestione/ordini-fornitore/${ordine.id}/inviato`, {}, { headers: getAuthHeaders() });
      toast.success('Ordine inviato!');
      loadOrdiniFornitore();
      loadDDTAttesa();
    } catch (error) {}
  };

  const eliminaOrdineFornitore = async (ordineId, e) => {
    e.stopPropagation(); // Evita che si apra il modal
    if (!window.confirm('Eliminare questo ordine?')) return;
    try {
      await axios.delete(`${API}/gestione/ordini-fornitore/${ordineId}`, { headers: getAuthHeaders() });
      toast.success('Eliminato');
      loadOrdiniFornitore();
    } catch (error) {
      toast.error('Errore');
    }
  };

  // ============================================
  // CARICAMENTO BOLLA CON ORDINE
  // ============================================
  
  const apriCaricamentoBolla = (ordine) => {
    setSelectedOrdine(ordine);
    setBollaAnalisi(null);
    setConfrontoOrdine(null);
    setShowBollaModal(true);
  };

  const handleUploadBolla = async (file) => {
    if (!selectedOrdine) return;
    
    setAnalyzingBolla(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result;
          const isPdf = file.type.includes('pdf');
          
          const endpoint = isPdf 
            ? `${API}/gestione/ddt/analizza-documento`
            : `${API}/gestione/ddt/analizza-immagine`;
          
          const payload = isPdf
            ? { file_base64: base64Data, tipo_file: 'pdf' }
            : { immagine_base64: base64Data };
          
          const res = await axios.post(endpoint, payload, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            timeout: 180000
          });
          
          if (res.data.success) {
            const analisi = res.data.analisi || res.data.dati;
            setBollaAnalisi(analisi);
            
            // Confronta con l'ordine
            confrontaBollaConOrdine(analisi, selectedOrdine);
            
            toast.success(`Analisi completata: ${analisi.prodotti?.length || 0} prodotti trovati`);
          } else {
            toast.error(res.data.detail || 'Errore analisi');
          }
        } catch (error) {
          console.error('Errore analisi:', error);
          toast.error('Errore analisi documento');
        } finally {
          setAnalyzingBolla(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore lettura file');
      setAnalyzingBolla(false);
    }
  };

  // Normalizza nome prodotto per confronto
  const normalizzaNome = (nome) => {
    if (!nome) return '';
    return nome.toUpperCase()
      .replace(/\s+IT$/i, '')
      .replace(/\s+PERU$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Confronta bolla ricevuta con ordine originale
  const confrontaBollaConOrdine = (analisi, ordine) => {
    const prodottiOrdinati = ordine.prodotti || [];
    const prodottiRicevuti = analisi.prodotti || [];
    
    const confrontoItems = [];
    let totaleOrdinato = 0;
    let totaleRicevuto = 0;
    let prodottiOk = 0;
    let prodottiMancanti = 0;
    let prodottiExtra = 0;
    let prodottiDifferenti = 0;
    
    // Mappa prodotti ricevuti per nome normalizzato
    const ricevutiMap = {};
    prodottiRicevuti.forEach(p => {
      const nomeNorm = normalizzaNome(p.nome);
      ricevutiMap[nomeNorm] = p;
      totaleRicevuto += parseFloat(p.importo || 0) || (parseFloat(p.quantita || 0) * parseFloat(p.prezzo_unitario || 0));
    });
    
    // Verifica ogni prodotto ordinato
    prodottiOrdinati.forEach(ordinato => {
      const nomeNorm = normalizzaNome(ordinato.nome);
      const ricevuto = ricevutiMap[nomeNorm];
      
      const importoOrdinato = parseFloat(ordinato.prezzo_stimato || 0) * parseFloat(ordinato.quantita || 0);
      totaleOrdinato += importoOrdinato;
      
      if (ricevuto) {
        // Prodotto trovato - verifica quantità e prezzo
        const qtaOrdinata = parseFloat(ordinato.quantita || 0);
        const qtaRicevuta = parseFloat(ricevuto.quantita || 0);
        const prezzoOrdinato = parseFloat(ordinato.prezzo_stimato || 0);
        const prezzoRicevuto = parseFloat(ricevuto.prezzo_unitario || 0);
        
        const diffQta = qtaRicevuta - qtaOrdinata;
        const diffPrezzo = prezzoOrdinato > 0 ? ((prezzoRicevuto - prezzoOrdinato) / prezzoOrdinato * 100) : 0;
        
        let status = 'ok';
        if (Math.abs(diffQta) > 0.01) {
          status = 'quantita_diversa';
          prodottiDifferenti++;
        } else if (Math.abs(diffPrezzo) > 5) {
          status = 'prezzo_diverso';
          prodottiDifferenti++;
        } else {
          prodottiOk++;
        }
        
        confrontoItems.push({
          nome: ordinato.nome,
          status,
          ordinato: { 
            quantita: qtaOrdinata, 
            unita: ordinato.unita_misura, 
            prezzo: prezzoOrdinato 
          },
          ricevuto: { 
            quantita: qtaRicevuta, 
            unita: ricevuto.unita_misura, 
            prezzo: prezzoRicevuto 
          },
          diffQta,
          diffPrezzo
        });
        
        // Rimuovi dalla mappa
        delete ricevutiMap[nomeNorm];
      } else {
        // Prodotto ordinato ma NON ricevuto (MANCANTE)
        prodottiMancanti++;
        confrontoItems.push({
          nome: ordinato.nome,
          status: 'mancante',
          ordinato: { 
            quantita: ordinato.quantita, 
            unita: ordinato.unita_misura, 
            prezzo: ordinato.prezzo_stimato 
          },
          ricevuto: null
        });
      }
    });
    
    // Prodotti ricevuti ma NON ordinati (EXTRA)
    Object.values(ricevutiMap).forEach(ricevuto => {
      prodottiExtra++;
      confrontoItems.push({
        nome: ricevuto.nome,
        status: 'extra',
        ordinato: null,
        ricevuto: { 
          quantita: ricevuto.quantita, 
          unita: ricevuto.unita_misura, 
          prezzo: ricevuto.prezzo_unitario 
        }
      });
    });
    
    // Determina il messaggio di riepilogo
    let riepilogo = '';
    let riepilogoType = 'success';
    
    if (prodottiMancanti === 0 && prodottiExtra === 0 && prodottiDifferenti === 0) {
      riepilogo = 'Ordine completo! Tutti i prodotti corrispondono.';
      riepilogoType = 'success';
    } else {
      const messaggi = [];
      if (prodottiMancanti > 0) {
        messaggi.push(`${prodottiMancanti} prodott${prodottiMancanti > 1 ? 'i mancanti' : 'o mancante'}`);
      }
      if (prodottiExtra > 0) {
        messaggi.push(`${prodottiExtra} prodott${prodottiExtra > 1 ? 'i extra' : 'o extra'}`);
      }
      if (prodottiDifferenti > 0) {
        messaggi.push(`${prodottiDifferenti} con quantità/prezzo diversi`);
      }
      riepilogo = 'Difformità rilevate: ' + messaggi.join(', ');
      riepilogoType = prodottiMancanti > 0 ? 'error' : 'warning';
    }
    
    setConfrontoOrdine({
      items: confrontoItems,
      totaleOrdinato,
      totaleRicevuto,
      prodottiOk,
      prodottiMancanti,
      prodottiExtra,
      prodottiDifferenti,
      riepilogo,
      riepilogoType
    });
  };

  // Salva bolla verificata
  const salvaBollaVerificata = async () => {
    if (!selectedOrdine || !bollaAnalisi) return;
    
    setAnalyzingBolla(true);
    try {
      // Aggiorna ordine come completato
      await axios.put(`${API}/gestione/ordini-fornitore/${selectedOrdine.id}/completato`, {
        bolla_ricevuta: true,
        prodotti_ricevuti: bollaAnalisi.prodotti || [],
        confronto: confrontoOrdine,
        fornitore_bolla: bollaAnalisi.fornitore,
        numero_bolla: bollaAnalisi.numero_ddt,
        data_bolla: bollaAnalisi.data_ddt
      }, { headers: getAuthHeaders() });
      
      toast.success('Bolla salvata!');
      chiudiBollaModal();
      loadOrdiniFornitore();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore salvataggio');
    } finally {
      setAnalyzingBolla(false);
    }
  };

  const chiudiBollaModal = () => {
    setShowBollaModal(false);
    setSelectedOrdine(null);
    setBollaAnalisi(null);
    setConfrontoOrdine(null);
  };

  // ============================================
  // CARICA DDT SENZA ORDINE
  // ============================================
  
  const handleUploadCaricaDDT = async (file) => {
    setAnalyzingCaricaDDT(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result;
          const isPdf = file.type.includes('pdf');
          
          const endpoint = isPdf 
            ? `${API}/gestione/ddt/analizza-documento`
            : `${API}/gestione/ddt/analizza-immagine`;
          
          const payload = isPdf
            ? { file_base64: base64Data, tipo_file: 'pdf' }
            : { immagine_base64: base64Data };
          
          const res = await axios.post(endpoint, payload, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            timeout: 180000
          });
          
          if (res.data.success) {
            const analisi = res.data.analisi || res.data.dati;
            setCaricaDDTAnalisi(analisi);
            toast.success(`${analisi.prodotti?.length || 0} prodotti trovati`);
          } else {
            toast.error(res.data.detail || 'Errore analisi');
          }
        } catch (error) {
          console.error('Errore analisi:', error);
          toast.error('Errore analisi documento');
        } finally {
          setAnalyzingCaricaDDT(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore lettura file');
      setAnalyzingCaricaDDT(false);
    }
  };

  const salvaCaricaDDT = async () => {
    if (!caricaDDTAnalisi) return;
    
    setAnalyzingCaricaDDT(true);
    try {
      // Crea nuovo DDT
      await axios.post(`${API}/gestione/ddt`, {
        status: 'ricevuto',
        fornitore: caricaDDTAnalisi.fornitore || 'Da definire',
        numero: caricaDDTAnalisi.numero_ddt || `AUTO-${Date.now()}`,
        data_ddt: caricaDDTAnalisi.data_ddt || new Date().toISOString().split('T')[0],
        data_ricezione: new Date().toISOString(),
        prodotti: caricaDDTAnalisi.prodotti || [],
        totale: caricaDDTAnalisi.totale || 0,
        note: 'Caricato manualmente senza ordine'
      }, { headers: getAuthHeaders() });
      
      toast.success('DDT salvato!');
      chiudiCaricaDDTModal();
      loadDDTAttesa();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore salvataggio');
    } finally {
      setAnalyzingCaricaDDT(false);
    }
  };

  const chiudiCaricaDDTModal = () => {
    setShowCaricaDDTModal(false);
    setCaricaDDTAnalisi(null);
  };

  // Import foto/testo
  const handleFotoListaSpesa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Image = reader.result.split(',')[1];
      setLoadingImport(true);
      setShowImportMenu(false);
      
      try {
        const res = await axios.post(`${API}/gestione/ordini/analizza-foto-lista`, {
          image_base64: base64Image
        }, { headers: getAuthHeaders() });
        
        setImportResults(res.data.prodotti || []);
        setShowImportResults(true);
        toast.success('Lista analizzata!');
      } catch (error) {
        toast.error('Errore analisi foto');
      } finally {
        setLoadingImport(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAnalizzaTesto = async () => {
    if (!testoImport.trim()) {
      toast.error('Inserisci del testo');
      return;
    }

    setLoadingImport(true);
    setShowImportTestoModal(false);

    try {
      const res = await axios.post(`${API}/gestione/ordini/importa-lista-testo`, {
        testo: testoImport
      }, { headers: getAuthHeaders() });
      
      setImportResults(res.data.prodotti || []);
      setShowImportResults(true);
      setTestoImport('');
      toast.success('Lista importata!');
    } catch (error) {
      toast.error('Errore importazione');
    } finally {
      setLoadingImport(false);
    }
  };

  const aggiungiProdottoDaImport = async (prodotto) => {
    try {
      await axios.post(`${API}/gestione/ordini/lista-spesa`, {
        nome: prodotto.nome || prodotto.nome_originale,
        quantita: prodotto.quantita || 1,
        unita_misura: prodotto.unita_misura || 'pz',
        fornitore_suggerito: prodotto.fornitore_suggerito,
        prezzo_stimato: prodotto.prezzo_stimato || 0
      }, { headers: getAuthHeaders() });
      
      toast.success(`${prodotto.nome || prodotto.nome_originale} aggiunto`);
      await loadListaSpesa();
      
      setImportResults(prev => prev.map(p => 
        p === prodotto ? { ...p, aggiunto: true } : p
      ));
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Raggruppa per fornitore
  const listaPerFornitore = listaSpesa.reduce((acc, item) => {
    const fornitore = item.fornitore_nome || 'Altro';
    if (!acc[fornitore]) acc[fornitore] = [];
    acc[fornitore].push(item);
    return acc;
  }, {});

  const totaleStimato = listaSpesa.reduce((sum, item) => sum + (item.quantita * item.prezzo_stimato), 0);

  // Funzione per ottenere lo stato dell'ordine
  const getOrdineStatus = (ordine) => {
    if (ordine.bolla_ricevuta) return { label: 'Completato', color: 'green' };
    if (ordine.stato === 'inviato') return { label: 'Inviato', color: 'blue' };
    return { label: 'Da Inviare', color: 'yellow' };
  };

  return (
    <TenantMobileLayout title="Ordini" onBack={onBack}>
      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('lista')}
          className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'lista'
              ? 'bg-cyan-500 text-black'
              : 'bg-black/40 text-gray-400 border border-cyan-500/30'
          }`}
          data-testid="tab-lista"
        >
          <ShoppingCart className="h-4 w-4" />
          Lista
          {listaSpesa.length > 0 && (
            <span className="px-1.5 py-0.5 bg-black/30 rounded-full text-xs">{listaSpesa.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('ordini')}
          className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'ordini'
              ? 'bg-blue-500 text-white'
              : 'bg-black/40 text-gray-400 border border-blue-500/30'
          }`}
          data-testid="tab-ordini"
        >
          <Send className="h-4 w-4" />
          Ordini
          {ordiniFornitore.length > 0 && (
            <span className="px-1.5 py-0.5 bg-black/30 rounded-full text-xs">{ordiniFornitore.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('ddt')}
          className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'ddt'
              ? 'bg-orange-500 text-white'
              : 'bg-black/40 text-gray-400 border border-orange-500/30'
          }`}
          data-testid="tab-ddt"
        >
          <Package className="h-4 w-4" />
          DDT
          {ddtAttesa.length > 0 && (
            <span className="px-1.5 py-0.5 bg-black/30 rounded-full text-xs">{ddtAttesa.length}</span>
          )}
        </button>
      </div>

      {/* TAB: Lista della Spesa */}
      {activeTab === 'lista' && (
        <div className="space-y-4">
          {/* Import & Search */}
          <div className="flex gap-2">
            <div className="relative">
              <button
                onClick={() => setShowImportMenu(!showImportMenu)}
                className="px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium flex items-center gap-2"
              >
                <Camera className="h-4 w-4" />
              </button>
              
              {showImportMenu && (
                <div className="absolute left-0 top-full mt-2 w-48 bg-black/90 border border-cyan-500/30 rounded-xl overflow-hidden z-50">
                  <label className="flex items-center gap-3 px-4 py-3 active:bg-white/10 cursor-pointer">
                    <Camera className="h-4 w-4 text-purple-400" />
                    <span className="text-white text-sm">Scatta Foto</span>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFotoListaSpesa} />
                  </label>
                  <label className="flex items-center gap-3 px-4 py-3 active:bg-white/10 cursor-pointer border-t border-cyan-500/20">
                    <Upload className="h-4 w-4 text-blue-400" />
                    <span className="text-white text-sm">Da Galleria</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleFotoListaSpesa} />
                  </label>
                  <button
                    onClick={() => { setShowImportMenu(false); setShowImportTestoModal(true); }}
                    className="w-full flex items-center gap-3 px-4 py-3 active:bg-white/10 border-t border-cyan-500/20"
                  >
                    <Edit className="h-4 w-4 text-amber-400" />
                    <span className="text-white text-sm">Scrivi Lista</span>
                  </button>
                </div>
              )}
            </div>
            
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                type="text"
                placeholder="Cerca prodotto..."
                value={searchProdotto}
                onChange={(e) => setSearchProdotto(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="search-prodotto"
              />
            </div>
          </div>

          {/* Suggerimenti ricerca */}
          {showSuggerimenti && suggerimenti.length > 0 && (
            <div className="bg-black/80 border border-cyan-500/30 rounded-xl overflow-hidden max-h-60 overflow-y-auto">
              {suggerimenti.map((prodotto, idx) => (
                <div key={idx} className="border-b border-cyan-500/20 last:border-0">
                  {prodotto.fornitori.map((fornitore, fIdx) => (
                    <div 
                      key={fIdx}
                      className={`px-3 py-3 active:bg-white/10 ${
                        fornitore.is_migliore ? 'bg-green-500/20 border-l-4 border-green-500' : ''
                      }`}
                      onClick={() => handleSelectProdotto(prodotto, fornitore)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{prodotto.nome}</p>
                          <p className="text-xs text-gray-400">
                            {fornitore.nome} • {formatCurrency(fornitore.ultimo_prezzo)}/{fornitore.unita_misura}
                            {fornitore.is_migliore && <span className="text-green-400 ml-2">Miglior prezzo</span>}
                          </p>
                        </div>
                        <span className={`font-bold ${fornitore.is_migliore ? 'text-green-400' : 'text-white'}`}>
                          {formatCurrency(fornitore.ultimo_prezzo)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Lista raggruppata per fornitore */}
          {listaSpesa.length > 0 ? (
            <div className="space-y-3">
              {Object.entries(listaPerFornitore).map(([fornitore, items]) => (
                <div 
                  key={fornitore} 
                  className="rounded-2xl overflow-hidden border border-cyan-500/30"
                  style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                >
                  <div className="px-3 py-2 bg-cyan-500/20 flex items-center gap-2">
                    <Package className="h-4 w-4 text-cyan-400" />
                    <span className="font-medium text-white">{fornitore}</span>
                    <span className="text-xs bg-cyan-500/30 text-cyan-300 px-2 py-0.5 rounded-full">
                      {items.length}
                    </span>
                  </div>
                  
                  <div className="divide-y divide-cyan-500/20">
                    {items.map((item) => (
                      <div key={item.id} className="px-3 py-3 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{item.nome}</p>
                          <p className="text-xs text-gray-400">
                            {item.quantita} {item.unita_misura} × {formatCurrency(item.prezzo_stimato)} = 
                            <span className="text-cyan-400 font-medium ml-1">
                              {formatCurrency(item.quantita * item.prezzo_stimato)}
                            </span>
                          </p>
                        </div>
                        <button 
                          onClick={() => rimuoviDallaLista(item.id)}
                          className="p-2 text-gray-500 active:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Totale e genera ordini */}
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-4 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-cyan-100 text-sm">Totale stimato</p>
                    <p className="text-2xl font-bold">{formatCurrency(totaleStimato)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-100 text-sm">{listaSpesa.length} prodotti</p>
                    <p className="text-lg font-medium">{Object.keys(listaPerFornitore).length} fornitori</p>
                  </div>
                </div>
                <button 
                  onClick={generaOrdiniPerFornitore}
                  disabled={loading}
                  className="w-full py-4 bg-white text-blue-600 rounded-xl font-semibold flex items-center justify-center gap-2 active:scale-98 transition-transform"
                >
                  {loading ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                  Genera Ordini
                </button>
              </div>
            </div>
          ) : (
            <div 
              className="text-center py-12 rounded-2xl border border-cyan-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
            >
              <ShoppingCart className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">Lista vuota</p>
              <p className="text-sm text-gray-500 mt-1">Cerca o importa per iniziare</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Ordini - Cliccando apre caricamento bolla */}
      {activeTab === 'ordini' && (
        <div className="space-y-3">
          {/* Tasto Nuova Bolla - naviga a Gestione Bolla */}
          <button
            onClick={() => setCurrentPage && setCurrentPage('bolle', { openNuovaBolla: true })}
            className="w-full py-4 rounded-2xl flex items-center justify-center gap-2 text-white font-semibold active:scale-98 transition-all"
            style={{ background: 'linear-gradient(135deg, rgba(147,51,234,1) 0%, rgba(79,70,229,1) 100%)' }}
            data-testid="btn-nuova-bolla"
          >
            <Plus className="h-5 w-5" />
            Nuova Bolla
          </button>
          
          <p className="text-sm text-gray-400 text-center">
            Tocca un ordine per caricare la bolla ricevuta
          </p>
          
          {ordiniFornitore.length > 0 ? (
            ordiniFornitore.map((ordine) => {
              const status = getOrdineStatus(ordine);
              return (
                <div 
                  key={ordine.id}
                  onClick={() => apriCaricamentoBolla(ordine)}
                  className={`rounded-2xl overflow-hidden border cursor-pointer active:scale-[0.98] transition-transform ${
                    status.color === 'green' ? 'border-green-500/30' :
                    status.color === 'blue' ? 'border-blue-500/30' :
                    'border-yellow-500/30'
                  }`}
                  style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-white">{ordine.fornitore_nome}</h3>
                        <p className="text-xs text-gray-400">
                          {ordine.prodotti?.length || 0} prodotti • {new Date(ordine.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          status.color === 'green' ? 'bg-green-500/20 text-green-400' :
                          status.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {status.label}
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-black/30 rounded-xl p-3 space-y-1 mb-3">
                      {ordine.prodotti?.slice(0, 3).map((p, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-300 truncate flex-1">{p.nome}</span>
                          <span className="text-white font-medium ml-2">{p.quantita} {p.unita_misura}</span>
                        </div>
                      ))}
                      {(ordine.prodotti?.length || 0) > 3 && (
                        <p className="text-xs text-gray-500">... e altri {ordine.prodotti.length - 3} prodotti</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          apriCaricamentoBolla(ordine);
                        }}
                        className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                      >
                        <Camera className="h-4 w-4" />
                        Carica Bolla
                      </button>
                      {ordine.whatsapp_link && !ordine.bolla_ricevuta && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            inviaWhatsApp(ordine);
                          }}
                          className="p-3 bg-green-500 text-white rounded-xl"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => eliminaOrdineFornitore(ordine.id, e)}
                        className="p-3 bg-red-500/20 text-red-400 rounded-xl"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div 
              className="text-center py-12 rounded-2xl border border-blue-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
            >
              <Send className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">Nessun ordine</p>
              <p className="text-sm text-gray-500 mt-1">Genera ordini dalla lista spesa</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: DDT */}
      {activeTab === 'ddt' && (
        <div className="space-y-3">
          {ddtAttesa.length > 0 ? (
            ddtAttesa.map((ddt) => (
              <div 
                key={ddt.id}
                className="rounded-2xl overflow-hidden border border-orange-500/30"
                style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white">DDT #{ddt.numero}</h3>
                      <p className="text-xs text-gray-400">
                        {ddt.fornitore} • {ddt.prodotti_attesi?.length || 0} prodotti attesi
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs font-medium">
                      In Attesa
                    </span>
                  </div>
                  
                  <div className="bg-orange-500/10 rounded-xl p-3 space-y-1 mb-3">
                    {ddt.prodotti_attesi?.map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-300">{p.nome}</span>
                        <span className="text-white font-medium">{p.quantita} {p.unita_misura}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                  >
                    <Camera className="h-4 w-4" />
                    Carica DDT Ricevuto
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div 
              className="text-center py-12 rounded-2xl border border-orange-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
            >
              <Package className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">Nessun DDT in attesa</p>
              <p className="text-sm text-gray-500 mt-1">I DDT vengono creati con gli ordini</p>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL: Caricamento Bolla con Confronto Ordine */}
      {/* ============================================ */}
      {showBollaModal && selectedOrdine && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[90vh] overflow-hidden flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            {/* Header */}
            <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {bollaAnalisi ? 'Verifica Bolla' : 'Carica Bolla'}
                </h2>
                <p className="text-sm text-gray-400">{selectedOrdine.fornitore_nome}</p>
              </div>
              <button onClick={chiudiBollaModal} className="p-2 bg-black/40 rounded-xl">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* STEP 1: Upload se non c'è ancora l'analisi */}
              {!bollaAnalisi ? (
                <>
                  {/* Prodotti dell'ordine */}
                  <div className="bg-blue-500/10 rounded-xl p-3 border border-blue-500/20">
                    <p className="text-xs font-medium text-blue-400 mb-2">Prodotti ordinati:</p>
                    {selectedOrdine.prodotti?.map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-gray-300">{p.nome}</span>
                        <span className="text-white font-medium">
                          {p.quantita} {p.unita_misura}
                          {p.prezzo_stimato > 0 && (
                            <span className="text-gray-500 ml-2">({formatCurrency(p.prezzo_stimato)})</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Upload buttons */}
                  <div className="space-y-3">
                    <label className="block">
                      <div className="w-full py-8 bg-black/40 border-2 border-dashed border-cyan-500/30 rounded-xl flex flex-col items-center justify-center gap-2 active:bg-white/5">
                        <Camera className="h-10 w-10 text-cyan-400" />
                        <span className="text-white font-medium">Scatta Foto Bolla</span>
                        <span className="text-xs text-gray-500">o tocca per caricare</span>
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment"
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadBolla(e.target.files[0])}
                      />
                    </label>
                    
                    <label className="block">
                      <div className="w-full py-4 bg-black/40 border border-cyan-500/30 rounded-xl flex items-center justify-center gap-2 active:bg-white/5">
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-300">Carica PDF</span>
                      </div>
                      <input 
                        type="file" 
                        accept=".pdf" 
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadBolla(e.target.files[0])}
                      />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* STEP 2: Risultati analisi con confronto */}
                  
                  {/* Messaggio riepilogo difformità */}
                  {confrontoOrdine && (
                    <div className={`p-4 rounded-xl border ${
                      confrontoOrdine.riepilogoType === 'success' 
                        ? 'bg-green-500/20 border-green-500/30' 
                        : confrontoOrdine.riepilogoType === 'error'
                          ? 'bg-red-500/20 border-red-500/30'
                          : 'bg-yellow-500/20 border-yellow-500/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        {confrontoOrdine.riepilogoType === 'success' ? (
                          <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0" />
                        ) : confrontoOrdine.riepilogoType === 'error' ? (
                          <XCircle className="h-6 w-6 text-red-400 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="h-6 w-6 text-yellow-400 flex-shrink-0" />
                        )}
                        <div>
                          <p className={`font-medium ${
                            confrontoOrdine.riepilogoType === 'success' ? 'text-green-400' :
                            confrontoOrdine.riepilogoType === 'error' ? 'text-red-400' :
                            'text-yellow-400'
                          }`}>
                            {confrontoOrdine.riepilogo}
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            {confrontoOrdine.prodottiOk} OK • {confrontoOrdine.prodottiMancanti} mancanti • {confrontoOrdine.prodottiExtra} extra
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Totali confronto */}
                  {confrontoOrdine && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                        <p className="text-xs text-gray-500">Totale Ordinato</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(confrontoOrdine.totaleOrdinato)}</p>
                      </div>
                      <div className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                        <p className="text-xs text-gray-500">Totale Bolla</p>
                        <p className={`text-lg font-bold ${
                          Math.abs(confrontoOrdine.totaleRicevuto - confrontoOrdine.totaleOrdinato) > 1 
                            ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {formatCurrency(confrontoOrdine.totaleRicevuto)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Lista prodotti con confronto */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-400">Dettaglio prodotti:</p>
                    
                    {confrontoOrdine?.items.map((item, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-xl border ${
                          item.status === 'ok' ? 'bg-green-500/10 border-green-500/30' :
                          item.status === 'mancante' ? 'bg-red-500/10 border-red-500/30' :
                          item.status === 'extra' ? 'bg-purple-500/10 border-purple-500/30' :
                          'bg-yellow-500/10 border-yellow-500/30'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            {item.status === 'ok' && <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />}
                            {item.status === 'mancante' && <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />}
                            {item.status === 'extra' && <Plus className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />}
                            {(item.status === 'quantita_diversa' || item.status === 'prezzo_diverso') && 
                              <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            }
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{item.nome}</p>
                              
                              {item.status === 'mancante' && (
                                <p className="text-xs text-red-400">
                                  Ordinato: {item.ordinato.quantita} {item.ordinato.unita} - NON RICEVUTO
                                </p>
                              )}
                              
                              {item.status === 'extra' && (
                                <p className="text-xs text-purple-400">
                                  NON ORDINATO - Ricevuto: {item.ricevuto.quantita} {item.ricevuto.unita}
                                </p>
                              )}
                              
                              {item.status === 'ok' && (
                                <p className="text-xs text-green-400">
                                  {item.ricevuto.quantita} {item.ricevuto.unita} • {formatCurrency(item.ricevuto.prezzo)}
                                </p>
                              )}
                              
                              {(item.status === 'quantita_diversa' || item.status === 'prezzo_diverso') && (
                                <div className="text-xs space-y-1 mt-1">
                                  <p className="text-gray-400">
                                    Ordinato: {item.ordinato.quantita} {item.ordinato.unita} × {formatCurrency(item.ordinato.prezzo)}
                                  </p>
                                  <p className="text-yellow-400 flex items-center gap-1">
                                    <ArrowRight className="h-3 w-3" />
                                    Ricevuto: {item.ricevuto.quantita} {item.ricevuto.unita} × {formatCurrency(item.ricevuto.prezzo)}
                                  </p>
                                  {Math.abs(item.diffQta) > 0.01 && (
                                    <p className={item.diffQta > 0 ? 'text-green-400' : 'text-red-400'}>
                                      Quantità: {item.diffQta > 0 ? '+' : ''}{item.diffQta.toFixed(2)} {item.ricevuto.unita}
                                    </p>
                                  )}
                                  {Math.abs(item.diffPrezzo) > 1 && (
                                    <p className={item.diffPrezzo > 0 ? 'text-red-400' : 'text-green-400'}>
                                      Prezzo: {item.diffPrezzo > 0 ? '+' : ''}{item.diffPrezzo.toFixed(1)}%
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Info bolla */}
                  {(bollaAnalisi.fornitore || bollaAnalisi.numero_ddt || bollaAnalisi.data_ddt) && (
                    <div className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                      <p className="text-xs text-gray-500 mb-1">Dati bolla estratti:</p>
                      {bollaAnalisi.fornitore && <p className="text-sm text-white">Fornitore: {bollaAnalisi.fornitore}</p>}
                      {bollaAnalisi.numero_ddt && <p className="text-sm text-white">N° DDT: {bollaAnalisi.numero_ddt}</p>}
                      {bollaAnalisi.data_ddt && <p className="text-sm text-white">Data: {bollaAnalisi.data_ddt}</p>}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Footer con bottone salva */}
            {bollaAnalisi && (
              <div className="p-4 border-t border-cyan-500/20 flex-shrink-0">
                <button
                  onClick={salvaBollaVerificata}
                  disabled={analyzingBolla}
                  className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 ${
                    confrontoOrdine?.riepilogoType === 'success' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-yellow-500 text-black'
                  }`}
                >
                  {analyzingBolla ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Check className="h-5 w-5" />
                  )}
                  {confrontoOrdine?.riepilogoType === 'success' 
                    ? 'Conferma Ricezione' 
                    : 'Conferma con Difformità'
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Quantità */}
      {showQuantitaModal && selectedProdotto && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[70vh] overflow-hidden"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Aggiungi alla Lista</h2>
              <button onClick={() => setShowQuantitaModal(false)} className="p-2 bg-black/40 rounded-xl">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                <p className="text-white font-medium">{selectedProdotto.prodotto.nome}</p>
                <p className="text-sm text-cyan-400">{selectedProdotto.fornitore.nome}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Quantità</label>
                <input
                  type="number"
                  value={quantita}
                  onChange={(e) => setQuantita(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-4 bg-black/40 border border-cyan-500/30 rounded-xl text-white text-2xl text-center font-bold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Unità</label>
                <div className="grid grid-cols-5 gap-2">
                  {['kg', 'pz', 'ct', 'lt', 'cs'].map((unit) => (
                    <button
                      key={unit}
                      onClick={() => setUnitaMisura(unit)}
                      className={`py-3 rounded-xl font-medium uppercase ${
                        unitaMisura === unit 
                          ? 'bg-cyan-500 text-black' 
                          : 'bg-black/40 text-gray-400 border border-cyan-500/30'
                      }`}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={aggiungiAllaLista}
                className="w-full py-4 bg-cyan-500 text-black rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Check className="h-5 w-5" />
                Aggiungi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Import Testo */}
      {showImportTestoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[80vh] overflow-hidden"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Scrivi Lista</h2>
              <button onClick={() => setShowImportTestoModal(false)} className="p-2 bg-black/40 rounded-xl">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <textarea
                value={testoImport}
                onChange={(e) => setTestoImport(e.target.value)}
                placeholder="Scrivi i prodotti separati da virgola o su righe diverse...

Es:
2kg pomodori
1 panna chef
gamberi rossi"
                rows={8}
                className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              />
              
              <button
                onClick={handleAnalizzaTesto}
                disabled={loadingImport}
                className="w-full py-4 bg-cyan-500 text-black rounded-xl font-bold flex items-center justify-center gap-2"
              >
                {loadingImport ? (
                  <RefreshCw className="h-5 w-5 animate-spin" />
                ) : (
                  <Check className="h-5 w-5" />
                )}
                Analizza
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Import Results */}
      {showImportResults && importResults && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-white">Prodotti Trovati ({importResults.length})</h2>
              <button onClick={() => setShowImportResults(false)} className="p-2 bg-black/40 rounded-xl">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {importResults.map((prodotto, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-xl border ${
                    prodotto.aggiunto 
                      ? 'bg-green-500/20 border-green-500/30' 
                      : 'bg-black/40 border-cyan-500/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{prodotto.nome || prodotto.nome_originale}</p>
                      <p className="text-xs text-gray-400">
                        {prodotto.quantita || 1} {prodotto.unita_misura || 'pz'}
                        {prodotto.fornitore_suggerito && ` • ${prodotto.fornitore_suggerito}`}
                      </p>
                    </div>
                    {prodotto.aggiunto ? (
                      <span className="text-green-400 text-sm">Aggiunto</span>
                    ) : (
                      <button
                        onClick={() => aggiungiProdottoDaImport(prodotto)}
                        className="px-4 py-2 bg-cyan-500 text-black rounded-lg font-medium text-sm"
                      >
                        Aggiungi
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-cyan-500/20 flex-shrink-0">
              <button
                onClick={() => setShowImportResults(false)}
                className="w-full py-4 bg-cyan-500 text-black rounded-xl font-bold"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL: Carica DDT senza ordine */}
      {/* ============================================ */}
      {showCaricaDDTModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[90vh] overflow-hidden flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-4 border-b border-purple-500/20 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {caricaDDTAnalisi ? 'Verifica DDT' : 'Carica DDT'}
                </h2>
                <p className="text-sm text-gray-400">Senza ordine associato</p>
              </div>
              <button onClick={chiudiCaricaDDTModal} className="p-2 bg-black/40 rounded-xl">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {!caricaDDTAnalisi ? (
                <>
                  {/* Info */}
                  <div className="bg-purple-500/10 rounded-xl p-3 border border-purple-500/20">
                    <p className="text-sm text-purple-400">
                      Carica una bolla/DDT ricevuta senza ordine associato. 
                      Verrà analizzata automaticamente.
                    </p>
                  </div>
                  
                  {/* Upload buttons */}
                  <div className="space-y-3">
                    <label className="block">
                      <div className="w-full py-8 bg-black/40 border-2 border-dashed border-purple-500/30 rounded-xl flex flex-col items-center justify-center gap-2 active:bg-white/5">
                        <Camera className="h-10 w-10 text-purple-400" />
                        <span className="text-white font-medium">Scatta Foto</span>
                        <span className="text-xs text-gray-500">o tocca per caricare</span>
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment"
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadCaricaDDT(e.target.files[0])}
                      />
                    </label>
                    
                    <label className="block">
                      <div className="w-full py-4 bg-black/40 border border-purple-500/30 rounded-xl flex items-center justify-center gap-2 active:bg-white/5">
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-300">Carica PDF</span>
                      </div>
                      <input 
                        type="file" 
                        accept=".pdf" 
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadCaricaDDT(e.target.files[0])}
                      />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* Risultati analisi */}
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                    <p className="text-green-400 font-medium mb-2">
                      Analisi completata: {caricaDDTAnalisi.prodotti?.length || 0} prodotti
                    </p>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <p className="text-xs text-gray-500">Fornitore</p>
                        <p className="text-white font-medium">{caricaDDTAnalisi.fornitore || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Data</p>
                        <p className="text-white font-medium">{caricaDDTAnalisi.data_ddt || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">N° DDT</p>
                        <p className="text-white font-medium">{caricaDDTAnalisi.numero_ddt || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Totale</p>
                        <p className="text-cyan-400 font-bold">{formatCurrency(caricaDDTAnalisi.totale)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lista prodotti estratti */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-400">Prodotti trovati:</p>
                    {caricaDDTAnalisi.prodotti?.map((p, idx) => (
                      <div key={idx} className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                        <div className="flex justify-between">
                          <span className="text-white flex-1">{p.nome}</span>
                          <span className="text-cyan-400 font-medium ml-2">
                            {p.quantita} {p.unita_misura}
                          </span>
                        </div>
                        {p.prezzo_unitario && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatCurrency(p.prezzo_unitario)}/u • Tot: {formatCurrency(p.importo || p.quantita * p.prezzo_unitario)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {caricaDDTAnalisi && (
              <div className="p-4 border-t border-purple-500/20 flex-shrink-0">
                <button
                  onClick={salvaCaricaDDT}
                  disabled={analyzingCaricaDDT}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  {analyzingCaricaDDT ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Check className="h-5 w-5" />
                  )}
                  Salva DDT
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {(loadingImport || analyzingBolla || analyzingCaricaDDT) && !bollaAnalisi && !caricaDDTAnalisi && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-center">
            <RefreshCw className="h-12 w-12 text-cyan-500 animate-spin mx-auto mb-4" />
            <p className="text-white">Analisi in corso...</p>
            <p className="text-sm text-gray-400 mt-1">Potrebbe richiedere alcuni secondi</p>
          </div>
        </div>
      )}
    </TenantMobileLayout>
  );
};

export default TenantMobileOrdini;
